/**
 * JSON paths of the Raw panel (W2-05): diagnostics and report entries name a place in the edited text by a path such
 * as `[3].easingType`, `notes[2].above` or `posControl[0].x`. These helpers turn such a path into a text offset (so
 * a problem can put the caret on the value it is about) and an offset into a line and column for display.
 */

import { parseJsonLossless } from '../../formats/json/lexer';
import type { JsonNode } from '../../formats/json/types';

export type PathToken = string | number;

/** `notes[2].above` -> ['notes', 2, 'above']; '' and '(root)' -> []. Unknown syntax yields the tokens read so far. */
export function pathTokens(path: string): PathToken[] {
  const out: PathToken[] = [];
  if (path === '' || path === '(root)') return out;
  const re = /\[(\d+)\]|\.?([^.[\]]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(path)) !== null) {
    if (m[1] !== undefined) out.push(Number(m[1]));
    else if (m[2] !== undefined) out.push(m[2]);
  }
  return out;
}

/** The deepest node of `root` along `path` (the root when nothing matches). */
export function nodeAtPath(root: JsonNode, path: string): JsonNode {
  let node = root;
  for (const tok of pathTokens(path)) {
    let next: JsonNode | undefined;
    if (typeof tok === 'number') {
      if (node.k === 'arr') next = node.items[tok];
    } else if (node.k === 'obj') {
      // Last occurrence wins, as when the JSON is read.
      for (const [k, v] of node.entries) if (k === tok) next = v;
    }
    if (next === undefined) break;
    node = next;
  }
  return node;
}

/** Offset of the value at `path` in `text`, or null when the text is not JSON. */
export function offsetOfPath(text: string, path: string): number | null {
  const parsed = parseJsonLossless(text);
  if (!parsed.ok) return null;
  return nodeAtPath(parsed.node, path).at;
}

/** 1-based line and column of an offset. */
export function lineColOf(text: string, offset: number): { readonly line: number; readonly col: number } {
  let line = 1;
  let start = 0;
  const end = Math.min(Math.max(offset, 0), text.length);
  for (let i = 0; i < end; i++) {
    if (text.charCodeAt(i) === 10) {
      line++;
      start = i + 1;
    }
  }
  return { line, col: end - start + 1 };
}
